import React, { useState, useEffect } from 'react';
import { Container, Col, Row, Button, Form, InputGroup } from 'react-bootstrap';
import SimpleHero from '../../components/SimpleHero/SimpleHero';
import myimg from '../../assets/images/gn_bank_cover.jpeg';
import './Location.css';
import { 
  FaMapMarkerAlt, 
  FaPhone, 
  FaDirections, 
  FaSearch,
  FaLocationArrow,
  FaClock,
  FaCreditCard,
  FaCar
} from 'react-icons/fa';

const Location = () => {
  const [userLocation, setUserLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [filterType, setFilterType] = useState('all'); // 'all', 'branch', 'atm'

  // Branch data - can be expanded with more branches
  const branches = [
    {
      id: 1,
      name: "GN Bank - 46th Street Branch",
      address: "4619 S King Drive, Chicago, IL 60653",
      phone: "(773) 624-2000",
      lat: 41.8086,
      lng: -87.6064,
      type: 'branch',
      hasATM: false,
      hasDriveUp: false,
      hours: [
        { day: "Monday - Friday", time: "10:00 AM - 3:00 PM" },
        { day: "Wednesday", time: "8:30 AM - 12:00 PM" },
        { day: "Saturday", time: "8:30 AM - 12:00 PM" },
        { day: "Sunday", time: "Closed" },
      ],
      // driveUpHours: [
      //   { day: "Monday - Friday", time: "10:00 AM - 3:00 PM" },
      //   { day: "Wednesday", time: "8:30 AM - 12:00 PM" },
      //   { day: "Saturday", time: "8:30 AM - 12:00 PM" },
      //   { day: "Sunday", time: "Closed" },
      // ],
    },
    // Add more branches here as needed
  ];

  // Get user's current location
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Unable to get your location. Please enter an address to search.');
        }
      );
    } else {
      alert('Geolocation is not supported by your browser.');
    }
  };

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return (R * c).toFixed(1);
  };

  // Get directions URL
  const getDirectionsUrl = (branch) => {
    const address = encodeURIComponent(branch.address);
    return `https://www.google.com/maps/dir/?api=1&destination=${address}`;
  };

  // Get Google Maps embed URL (works without API key using iframe)
  const getMapEmbedUrl = (branch) => {
    // Using Google Maps iframe embed - works without API key
    const address = encodeURIComponent(branch.address);
    return `https://maps.google.com/maps?q=${address}&output=embed&z=15`;
  };

  // Filter branches based on search and filter type
  const filteredBranches = branches.filter(branch => {
    const matchesSearch = 
      branch.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      branch.address.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = 
      filterType === 'all' ||
      (filterType === 'branch' && branch.type === 'branch') ||
      (filterType === 'atm' && branch.hasATM);

    return matchesSearch && matchesFilter;
  });

  // Sort by distance if user location is available
  const sortedBranches = userLocation
    ? [...filteredBranches].sort((a, b) => {
        const distA = calculateDistance(userLocation.lat, userLocation.lng, a.lat, a.lng);
        const distB = calculateDistance(userLocation.lat, userLocation.lng, b.lat, b.lng);
        return distA - distB;
      })
    : filteredBranches;

  useEffect(() => {
    // Set first branch as selected by default
    if (sortedBranches.length > 0 && !selectedBranch) {
      setSelectedBranch(sortedBranches[0]);
    }
  }, [sortedBranches]);

  const handleBranchSelect = (branch) => {
    setSelectedBranch(branch);
  };

  const defaultBranch = branches[0];

  return (
    <>
      <SimpleHero
        title="Find a branch or ATM"
        subtitle={`${defaultBranch.name} · ${defaultBranch.address} · ${defaultBranch.phone}`}
        primaryLabel="Get directions"
        primaryHref={getDirectionsUrl(defaultBranch)}
        variant="sofi"
        imageSrc={myimg}
        imageAlt="GN Bank branch"
        imageOn="right"
        tiltImage
        badges={["Lobby hours (excluding federal holidays)", "Drive‑up hours", "ATMs"]}
      />

      <Container className="py-5">
        {/* Search and Filter Section */}
        <Row className="mb-4">
          <Col md={12}>
            <div className="location-search-section">
              <Row className="g-3">
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search by address, city, or zip code..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <option value="all">All Locations</option>
                    <option value="branch">Branches Only</option>
                    <option value="atm">ATMs Only</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Button
                    variant="outline-primary"
                    className="w-100"
                    onClick={getCurrentLocation}
                  >
                    <FaLocationArrow className="me-2" />
                    Use My Location
                  </Button>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        <Row>
          {/* Branch List */}
          <Col lg={5} className="mb-4">
            <div className="branch-list-container">
              <h3 className="mb-3 gn-title">Locations Near You</h3>
              {userLocation && (
                <p className="text-muted mb-3">
                  <FaLocationArrow className="me-2" />
                  Showing distances from your location
                </p>
              )}
              
              {sortedBranches.length > 0 ? (
                <div className="branch-cards">
                  {sortedBranches.map((branch) => {
                    const distance = userLocation
                      ? calculateDistance(userLocation.lat, userLocation.lng, branch.lat, branch.lng)
                      : null;
                    
                    return (
                      <div
                        key={branch.id}
                        className={`branch-card ${selectedBranch?.id === branch.id ? 'active' : ''}`}
                        onClick={() => handleBranchSelect(branch)}
                      >
                        <div className="branch-card-header">
                          <div className="branch-icon">
                            <FaMapMarkerAlt />
                          </div>
                          <div className="branch-info">
                            <h4 className="branch-name">{branch.name}</h4>
                            <p className="branch-address">
                              <FaMapMarkerAlt className="me-2" />
                              {branch.address}
                            </p>
                            {distance && (
                              <p className="branch-distance">
                                <FaLocationArrow className="me-2" />
                                {distance} miles away
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="branch-card-footer">
                          <div className="branch-features">
                            {branch.type === 'branch' && (
                              <span className="feature-badge">
                                <FaCreditCard className="me-1" />
                                Branch
                              </span>
                            )}
                            {branch.hasATM && (
                              <span className="feature-badge">
                                <FaCreditCard className="me-1" />
                                ATM
                              </span>
                            )}
                            {branch.hasDriveUp && (
                              <span className="feature-badge">
                                <FaCar className="me-1" />
                                Drive-Up
                              </span>
                            )}
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(getDirectionsUrl(branch), '_blank');
                            }}
                          >
                            <FaDirections className="me-2" />
                            Directions
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-results">
                  <p>No locations found matching your search.</p>
                </div>
              )}
            </div>
          </Col>

          {/* Map and Branch Details */}
          <Col lg={7}>
            {selectedBranch && (
              <>
                {/* Map */}
                <div className="map-container mb-4">
                  <iframe
                    src={getMapEmbedUrl(selectedBranch)}
                    width="100%"
                    height="400"
                    style={{ border: 0, borderRadius: '12px' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={`Map of ${selectedBranch.name}`}
                  ></iframe>
                </div>

                {/* Branch Details */}
                <div className="branch-details">
                  <h2 className="gn-title mb-3">{selectedBranch.name}</h2>
                  
                  <div className="branch-contact-info mb-4">
                    <p className="mb-2">
                      <FaMapMarkerAlt className="me-2 text-primary" />
                      <strong>Address:</strong> {selectedBranch.address}
                    </p>
                    <p className="mb-2">
                      <FaPhone className="me-2 text-primary" />
                      <strong>Phone:</strong>{' '}
                      <a href={`tel:${selectedBranch.phone}`}>{selectedBranch.phone}</a>
                    </p>
                    <Button
                      variant="primary"
                      className="btn-gn-primary mt-2"
                      onClick={() => window.open(getDirectionsUrl(selectedBranch), '_blank')}
                    >
                      <FaDirections className="me-2" />
                      Get Directions
                    </Button>
                  </div>

                  <Row>
                    <Col md={12}>
                      <div className="hours-section">
                        <h4 className="gn-title mb-3">
                          <FaClock className="me-2" />
                          Lobby Hours (excluding federal holidays)
                        </h4>
                        <ul className="hours-list">
                          {selectedBranch.hours.map((hour, index) => (
                            <li key={index}>
                              <strong>{hour.day}:</strong> {hour.time}
                            </li>
                          ))}
                        </ul>
                        <p className="hours-excluding text-muted small mb-0 mt-2">Excluding federal holidays.</p>
                      </div>
                    </Col>
                    {selectedBranch.hasDriveUp && (
                      <Col md={6}>
                        {/* <div className="hours-section">
                          <h4 className="gn-title mb-3">
                            <FaCar className="me-2" />
                            Drive-Up Hours (excluding federal holidays)
                          </h4>
                          <ul className="hours-list">
                            {selectedBranch.driveUpHours.map((hour, index) => (
                              <li key={index}>
                                <strong>{hour.day}:</strong> {hour.time}
                              </li>
                            ))}
                          </ul>
                          <p className="hours-excluding text-muted small mb-0 mt-2">Excluding federal holidays.</p>
                        </div> */}
                      </Col>
                    )}
                  </Row>
                </div>
              </>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Location;
